import React, { useContext, useState } from 'react'
import { Typography, Box } from '@material-ui/core'
import { Formik, Form, FormikValues } from 'formik'
import {
  updateUserAttribute,
  confirmUserAttribute,
  fetchUserAttributes,
} from 'aws-amplify/auth'
import Yup from '../../utils/lang/ValidationTranslation'
import CustomTranslations from '../../utils/lang/CustomTranslations'
import { SnackbarContext } from '../../context/Snackbar'
import UserContext, { User } from '../../context/User'
import FormikTextField from '../Input/FormikTextField'
import CustomButton from '../Button/CustomButton'

const validationSchema = Yup.object().shape({
  newEmail: Yup.string().email().required(),
})

const ChangeEmail = (): JSX.Element => {
  const { user, updateUser } = useContext(UserContext)
  const { openSnackbar, openSnackbarError } = useContext(SnackbarContext)
  const [verifyEmail, setVerifyEmail] = useState<boolean>(false)
  const {
    EMAIL_SAVED,
    EMAIL_VERIFIED,
    CHANGE_EMAIL,
    NEW_EMAIL_LABEL,
    NEW_EMAIL_PLACEHOLDER,
    VERIFY_EMAIL,
  } = CustomTranslations

  const handleUpdate = async (values: FormikValues): Promise<void> => {
    try {
      await updateUserAttribute({
        userAttribute: {
          attributeKey: 'email',
          value: values.newEmail,
        },
      })

      openSnackbar(EMAIL_SAVED, 'success')

      if (user?.email !== values.newEmail) {
        setVerifyEmail(true)
        updateUser({
          ...user,
          email: values.newEmail,
          emailVerified: false,
        } as User)
      } else {
        setVerifyEmail(false)
      }
    } catch (err: any) {
      openSnackbarError(err)
    }
  }

  const handleVerification = async (
    values: FormikValues,
    reset: () => void
  ): Promise<void> => {
    try {
      await confirmUserAttribute({
        userAttributeKey: 'email',
        confirmationCode: values.verificationCode,
      })

      openSnackbar(EMAIL_VERIFIED, 'success')
      setVerifyEmail(false)

      // Refresh user attributes
      const attributes = await fetchUserAttributes()
      updateUser({
        ...user,
        emailVerified: attributes.email_verified === 'true',
      } as User)

      reset()
    } catch (err: any) {
      openSnackbarError(err)
    }
  }

  return (
    <>
      <Typography variant="h6" gutterBottom>
        {CHANGE_EMAIL}
      </Typography>
      <Formik
        enableReinitialize
        initialValues={{
          newEmail: '',
          verificationCode: '',
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }): void => {
          if (verifyEmail) {
            handleVerification(values, resetForm)
          } else {
            handleUpdate(values)
          }
        }}
      >
        {({ submitForm, isValid }): JSX.Element => (
          <>
            <Form>
              <FormikTextField
                name="newEmail"
                label={NEW_EMAIL_LABEL}
                placeholder={NEW_EMAIL_PLACEHOLDER}
                margin="normal"
                disabled={false}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  autoComplete: 'email',
                }}
              />
              {verifyEmail && (
                <FormikTextField
                  name="verificationCode"
                  label="Bestätigungscode"
                  placeholder="Bestätigungscode eingeben"
                  type="string"
                  margin="normal"
                  disabled={false}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            </Form>
            <Box my={3}>
              <CustomButton disabled={!isValid} onClick={submitForm}>
                {verifyEmail ? VERIFY_EMAIL : CHANGE_EMAIL}
              </CustomButton>
            </Box>
          </>
        )}
      </Formik>
    </>
  )
}

export default ChangeEmail
